"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useIsLens } from "@/lib/audienceLens/AudienceLensContext";

/**
 * M3 — Banker-lens evidence panel. Surfaces the ISO 20022 / SWIFT MT
 * progression + take-rate / settlement-finality detail that a
 * trade-finance banker actually cares about. Renders ONLY when the
 * URL lens is `?lens=banker`; falls through to nothing otherwise so
 * the external demo's narrative pace isn't disturbed.
 *
 * Variants:
 *
 *   variant="lc-issued"    — full MT 700 → MT 710 → MT 754 → MT 756
 *                            timeline + Orbitra take-rate breakdown +
 *                            settlement finality assertion.
 *
 *   variant="lc-composer"  — structured-credit detail: interest pricing,
 *                            collateral assumed, tenor risk premium,
 *                            covenant package.
 *
 *   variant="gate-cascade" — bank-side gate signals: effective-ceiling
 *                            derivation, AML cycle freshness, sanctions
 *                            list versions, manual-review flags.
 *
 *   variant="settlement"   — reimbursement chain detail: MT 756 advice
 *                            of reimbursement, settlement finality
 *                            anchor, T+0 vs T+1 vs T+2 commitment.
 */
export function BankerEvidence({
  variant,
  amountUsd,
  bankBadge,
  advisingBank,
  className,
}: {
  variant: "lc-issued" | "lc-composer" | "gate-cascade" | "settlement";
  amountUsd?: number;
  bankBadge?: string | null;
  advisingBank?: string;
  className?: string;
}) {
  const t = useTranslations("bankerEvidence");
  const isBanker = useIsLens("banker");
  if (!isBanker) return null;
  const dash = t("dashPlaceholder");

  if (variant === "lc-issued") {
    // Take-rate differs by funding path: bank-LC = 0.85% (split with bank
    // rails); Programmable Escrow = 0.55% (platform-only, no bank rail
    // cut). Both flow into the same finality anchor.
    const isEscrow = !bankBadge;
    const orbitraTakePct = isEscrow ? 0.55 : 0.85;
    const takeUsd = amountUsd ? Math.round((amountUsd * orbitraTakePct) / 100) : null;
    return (
      <div className={clsx("glass p-4", className)}>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            {t("kicker")}
          </p>
          <span className="rounded-full border border-state-financed/30 bg-state-financed/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-financed">
            {t("lensChip")}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4">
          {isEscrow ? (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
                {t("escrowTitle")}
              </p>
              <ul className="mt-2 flex flex-col gap-1 text-[11px] text-orbitra-paper">
                <li>{t("escrowMtLine1")}</li>
                <li>{t("escrowMtLine2")}</li>
                <li>{t("escrowMtLine3Prefix")} <span className="font-mono text-state-financed">{t("escrowMtLine3Value")}</span> {t("escrowMtLine3Suffix")}</li>
                <li className="text-state-financed">{t("escrowMtLine4")}</li>
              </ul>
              <p className="mt-2 text-[10px] text-orbitra-muted">
                {t("escrowMtTakeaway")}
              </p>
            </div>
          ) : (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
                {t("mtProgressionTitle")}
              </p>
              <ul className="mt-2 flex flex-col gap-1 text-[11px]">
                <MtRow code="MT 700" label={t("mt700Label")} state="done" detail={t("mt700Detail", { issuer: bankBadge, advising: advisingBank ?? t("mtDefaultAdvising") })} />
                <MtRow code="MT 710" label={t("mt710Label")} state="done" detail={advisingBank ? t("mt710DetailAdvised", { advising: advisingBank }) : t("mt710DetailUnknown")} />
                <MtRow code="MT 754" label={t("mt754Label")} state="pending" detail={t("mt754Detail")} />
                <MtRow code="MT 756" label={t("mt756Label")} state="pending" detail={t("mt756Detail")} />
              </ul>
            </div>
          )}

          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("takeRateTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-2 text-[11px]">
              <li className="rounded border border-state-financed/30 bg-state-financed/8 px-2 py-1.5">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("takeRateChip")}</p>
                <p className="mt-0.5 font-display text-sm font-semibold text-orbitra-paper">
                  {takeUsd !== null ? `USD ${takeUsd.toLocaleString()}` : dash}
                  <span className="ms-2 font-mono text-[10px] text-orbitra-muted">{t("takeRateSuffix", { pct: orbitraTakePct })}</span>
                </p>
                <p className="mt-0.5 text-[10px] text-orbitra-muted">
                  {isEscrow ? t("takeRateSplitEscrow") : t("takeRateSplitBank")}
                </p>
              </li>
              <li className="rounded border border-state-traced/30 bg-state-traced/8 px-2 py-1.5">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("finalityChip")}</p>
                <p className="mt-0.5 text-orbitra-paper">
                  {t("finalityBody")}
                  {" "}
                  {isEscrow ? t("finalitySuffixEscrow") : t("finalitySuffixBank")}
                </p>
              </li>
            </ul>
          </div>
        </div>

        <BankerFooter />
      </div>
    );
  }

  if (variant === "lc-composer") {
    const isEscrow = !bankBadge;
    return (
      <div className={clsx("glass p-4", className)}>
        <BankerHeader />
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          {isEscrow ? (
            <>
              <BankerField label={t("composerFieldPricing")}      value={t("composerEscrowPricing")} />
              <BankerField label={t("composerFieldApr")}          value={t("composerEscrowApr")} />
              <BankerField label={t("composerFieldCollateral")}   value={t("composerEscrowCollateral")} />
              <BankerField label={t("composerFieldExposure")}     value={t("composerEscrowExposure")} />
              <BankerField label={t("composerFieldOnboarding")}   value={t("composerEscrowOnboarding")} />
              <BankerField label={t("composerFieldCovenants")}    value={t("composerEscrowCovenants")} />
            </>
          ) : (
            <>
              <BankerField label={t("composerFieldPricing")}      value={t("composerBankPricing")} />
              <BankerField label={t("composerFieldApr")}          value={t("composerBankApr")} />
              <BankerField label={t("composerFieldCollateral")}   value={t("composerBankCollateral", { bank: bankBadge ?? "" })} />
              <BankerField
                label={t("composerFieldExposure")}
                value={
                  bankBadge === "FAB"
                    ? t("composerBankExposureFab")
                    : bankBadge === "Itau"
                      ? t("composerBankExposureItau")
                      : t("composerBankExposureOther", { bank: bankBadge ?? "" })
                }
              />
              <BankerField label={t("composerFieldTenorRisk")}    value={t("composerBankTenorRisk")} />
              <BankerField label={t("composerFieldCovenants")}    value={t("composerBankCovenants")} />
            </>
          )}
        </div>
        <BankerFooter />
      </div>
    );
  }

  if (variant === "gate-cascade") {
    const isEscrow = !bankBadge;
    return (
      <div className={clsx("glass p-4", className)}>
        <BankerHeader />
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {isEscrow ? t("gateEscrowTitle") : t("gateBankTitle")}
            </p>
            {isEscrow ? (
              <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
                <li>{t("gateNotionalLabel")} <span className="font-mono text-state-financed">USD {amountUsd?.toLocaleString() ?? dash}</span></li>
                <li>{t("gateEscrowBuyerFunds")} <span className="font-mono">{t("gateEscrowBuyerFundsValue")}</span> {t("gateEscrowBuyerFundsSuffix")}</li>
                <li>{t("gateEscrowGuarantee")} <span className="font-mono">{t("gateEscrowGuaranteeValue")}</span></li>
                <li>{t("gateEscrowNetExposure")} <span className="font-mono text-policy-pass">{t("gateEscrowNetExposureValue")}</span></li>
                <li>{t("gateEscrowOverride")} <span className="font-mono">{t("gateEscrowOverrideValue")}</span></li>
                <li className="text-policy-pass">{t("gateEscrowFooter")}</li>
              </ul>
            ) : (
              <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
                <li>{t("gateNotionalLabel")} <span className="font-mono text-state-financed">USD {amountUsd?.toLocaleString() ?? dash}</span></li>
                <li>{t("gateBankBuyerCapital")} <span className="font-mono">{t("gateBankBuyerCapitalValue")}</span></li>
                <li>{t("gateBankGuarantee")} <span className="font-mono">{t("gateBankGuaranteeValue", { bank: bankBadge ?? "" })}</span></li>
                <li>{t("gateBankNetExposure")} <span className="font-mono text-state-financed">USD {amountUsd ? Math.round(amountUsd * 0.3).toLocaleString() : dash}</span></li>
                <li>{t("gateBankCeiling")} <span className="font-mono">{t("gateBankCeilingValue")}</span></li>
                <li className="text-policy-pass">{t("gateBankFooter")}</li>
              </ul>
            )}
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("screeningTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-[10px] text-orbitra-paper">
              <li>{t("screeningOfac")}</li>
              <li>{t("screeningEu")}</li>
              <li>{t("screeningUn")}</li>
              <li>{t("screeningUk")}</li>
              <li>{t("screeningLocal")}</li>
            </ul>
            <p className="mt-2 text-[10px] text-state-financed">
              {t("screeningQueueNote")}
            </p>
          </div>
        </div>
        <BankerFooter />
      </div>
    );
  }

  if (variant === "settlement") {
    const isEscrow = !bankBadge;
    return (
      <div className={clsx("glass p-4", className)}>
        <BankerHeader />
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {isEscrow ? t("settlementEscrowTitle") : t("settlementBankTitle")}
            </p>
            {isEscrow ? (
              <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
                <li>{t("settlementEscrowTrigger")} <span className="font-mono text-state-financed">{t("settlementEscrowTriggerValue")}</span></li>
                <li>{t("settlementEscrowRelease")} <span className="font-mono">{t("settlementEscrowReleaseValue")}</span></li>
                <li>{t("settlementLegs")} <span className="font-mono">{t("settlementLegsValue")}</span></li>
                <li>{t("settlementRail")} <span className="font-mono text-state-financed">{t("settlementRailValue")}</span></li>
                <li>{t("settlementReconciliation")} <span className="font-mono">{t("settlementReconciliationValue")}</span> ✓</li>
                <li className="text-state-financed">{t("settlementEscrowPnl")}</li>
              </ul>
            ) : (
              <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
                <li>{t("settlementMt754")} <span className="font-mono">{t("settlementMt754Value")}</span> ✓</li>
                <li>{t("settlementMt756")} <span className="font-mono">{t("settlementMt756Value")}</span> ✓</li>
                <li>{t("settlementLegs")} <span className="font-mono">{t("settlementLegsValue")}</span></li>
                <li>{t("settlementRail")} <span className="font-mono text-state-financed">{t("settlementRailValue")}</span></li>
                <li>{t("settlementReconciliation")} <span className="font-mono">{t("settlementReconciliationValue")}</span> ✓</li>
              </ul>
            )}
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("finalityTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("finalityMoment")} <span className="font-mono text-state-financed">{t("finalityMomentValue")}</span></li>
              <li>{t("finalityAnchor")} <span className="font-mono">{t("finalityAnchorValue")}</span></li>
              <li>{t("finalityDesk")}</li>
              <li>{t("finalityDispute")} <span className="font-mono">{t("finalityDisputeValue")}</span></li>
              <li>{t("finalityRetention")} <span className="font-mono">{t("finalityRetentionValue")}</span></li>
            </ul>
          </div>
        </div>
        <BankerFooter />
      </div>
    );
  }

  return null;
}

function BankerHeader() {
  const t = useTranslations("bankerEvidence");
  return (
    <div className="flex items-baseline justify-between">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
        {t("kicker")}
      </p>
      <span className="rounded-full border border-state-financed/30 bg-state-financed/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-financed">
        {t("lensChip")}
      </span>
    </div>
  );
}

function BankerFooter() {
  const t = useTranslations("bankerEvidence");
  return (
    <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
      {t("footerPrefix")}
      <span className="ms-1 font-mono text-state-financed">{t("footerToggle")}</span>{t("footerToggleSuffix")}
    </p>
  );
}

function BankerField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-state-financed/20 bg-orbitra-ink/40 px-2 py-1.5">
      <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}

function MtRow({ code, label, state, detail }: { code: string; label: string; state: "done" | "pending"; detail: string }) {
  return (
    <li className={clsx(
      "flex items-center gap-2 rounded border px-2 py-1",
      state === "done"
        ? "border-policy-pass/40 bg-policy-pass/8 text-policy-pass"
        : "border-state-future/40 bg-state-future/8 text-state-future",
    )}>
      <span className="font-mono text-[10px] uppercase tracking-wider">{code}</span>
      <span className="flex-1 truncate text-orbitra-paper">{label}</span>
      <span className="font-mono text-[9px] text-orbitra-muted">{detail}</span>
      <span className="font-mono text-[9px]">{state === "done" ? "✓" : "○"}</span>
    </li>
  );
}
